// src/pages/settings/components/UserSettings.jsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  InputAdornment,
  IconButton,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import SecureAvatar from '../../../components/ui/SecureAvatar';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  PhotoCamera as CameraIcon,
  EmailOutlined as EmailIcon,
  PersonOutline as PersonIcon,
  PhoneOutlined as PhoneIcon,
  LocationOnOutlined as LocationIcon,
} from '@mui/icons-material';

// Import stores
import useAuthStore from '../../../stores/authStore';
import useSettingsStore from '../../../stores/settingsStore';
import useNotificationStore from '../../../stores/notificationStore';

const UserSettings = () => {
  const theme = useTheme();
  const { user, updateUserProfile, isLoading: authLoading } = useAuthStore();
  const { settings } = useSettingsStore();
  const { showSuccess, showError } = useNotificationStore();
  
  // Local state
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    jobTitle: user?.jobTitle || '',
    location: user?.location || '',
    bio: user?.bio || '',
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle profile photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.includes('image/')) {
      showError('Please upload an image file');
      return;
    }
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showError('Image size should not exceed 2MB');
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Import the upload service
      const uploadService = await import('../../../services/uploadService').then(module => module.default);
      
      // Create a form data object to send the file
      const formData = new FormData();
      formData.append('file', file);
      formData.append('prefix', 'user-avatars');
      
      // Upload the image using the uploadBrandLogo method which is set up to handle uploads
      // We're using this method as it's already configured properly
      const uploadResult = await uploadService.uploadFile(file, {
        prefix: 'user-avatars',
        generateSignedUrl: true,
        metadata: {
          userId: user?._id || 'unknown',
          contentType: 'profile-photo'
        }
      });
      
      // The photo key is what we want to store in the user profile
      // This will be handled by the SecureAvatar component
      await updateUserProfile({
        photo: uploadResult.key
      });
      
      showSuccess('Profile photo updated successfully');
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      showError(error.message || 'Failed to upload profile photo');
    } finally {
      setIsUploading(false);
    }
  };
  
  // Toggle edit mode
  const handleToggleEdit = () => {
    if (isEditing) {
      // Cancel editing
      setFormData({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: user?.phone || '',
        jobTitle: user?.jobTitle || '',
        location: user?.location || '',
        bio: user?.bio || '',
      });
    }
    setIsEditing(!isEditing);
  };
  
  // Save profile changes
  const handleSaveProfile = async () => {
    try {
      await updateUserProfile(formData);
      showSuccess('Profile updated successfully');
      setIsEditing(false);
    } catch (error) {
      showError(error.message || 'Failed to update profile');
    }
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">User Profile</Typography>
        <Button
          variant={isEditing ? 'outlined' : 'contained'}
          color={isEditing ? 'secondary' : 'primary'}
          startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
          onClick={isEditing ? handleSaveProfile : handleToggleEdit}
          disabled={authLoading}
        >
          {isEditing ? 'Save Changes' : 'Edit Profile'}
        </Button>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Profile Photo Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', mb: 4 }}>
        <Box sx={{ position: 'relative', mr: { xs: 0, sm: 4 }, mb: { xs: 3, sm: 0 } }}>
          <SecureAvatar
            src={user?.photo}
            alt={`${formData.firstName} ${formData.lastName}`}
            sx={{ 
              width: 120, 
              height: 120,
              border: `4px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            {formData.firstName?.charAt(0) || ''}{formData.lastName?.charAt(0) || ''}
          </SecureAvatar>
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              borderRadius: '50%',
              padding: '8px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
            onClick={() => fileInputRef.current.click()}
          >
            {isUploading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              <CameraIcon sx={{ color: 'white', fontSize: 20 }} />
            )}
          </Box>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handlePhotoUpload}
          />
        </Box>
        
        <Box>
          <Typography variant="h5" gutterBottom>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {user?.jobTitle || 'No title specified'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {user?.email}
          </Typography>
        </Box>
      </Box>
      
      {/* Profile Form */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            fullWidth
            disabled={!isEditing}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            fullWidth
            disabled={!isEditing}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            disabled={true} // Email is typically not editable
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            fullWidth
            disabled={!isEditing}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            label="Job Title"
            name="jobTitle"
            value={formData.jobTitle}
            onChange={handleChange}
            fullWidth
            disabled={!isEditing}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            fullWidth
            disabled={!isEditing}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            label="Bio"
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            fullWidth
            disabled={!isEditing}
            multiline
            rows={4}
            helperText="Tell us a bit about yourself"
          />
        </Grid>
      </Grid>
      
      {isEditing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleToggleEdit}
            disabled={authLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            
            startIcon={<SaveIcon />}
            onClick={handleSaveProfile}
            disabled={authLoading}
          >
            {authLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} /> Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default UserSettings;